

































































































import Vue from 'vue';
import { ComponentHelper, RouteHelpers, ModalHelpers } from '@/mixins';
import MStoreGroupedTable from '@/components/shared/Table/MStoreGroupedTable.vue';
import LoadingOverlay from '@/components/shared/LoadingOverlay.vue';
import ApiConfiguration from '@/classes/ApiConfiguration';
import Toast from '@/classes/SnotifyConfiguration';
import { ColourVariantType, ExceptionalJobModalState } from '@/types';
import {
  ExceptionalJobConfigurationModel,
  UpdateExceptionalJobConfigurationModel,
  ExceptionalJobVersionType,
  ExceptionalJobConfigurationCabinetModel
} from '@/services/GeneratedApi';
import { EnumUtils } from '@/utils';

export default Vue.extend({
  name: 'ExceptionalJobs',
  components: {
    MStoreGroupedTable,
    LoadingOverlay
  },
  mixins: [ComponentHelper, RouteHelpers, ModalHelpers],
  data() {
    return {
      isLoading: false,
      exceptionalJobsGrouped: { groupedConfigurations: [] } as ExceptionalJobConfigurationCabinetModel,
      groupByField: 'cabinetDescription',
      searchQuery: '',
      showConfigureCabinetModal: false,
      modalMode: ExceptionalJobModalState.CreateCabinetConfig,
      cabinetId: '',
      documentTypeIds: [] as number[],
      versionType: ExceptionalJobVersionType.CreateNewVersion as ExceptionalJobVersionType,
      enabled: false,
      documentTypeSelectFilter: '',
      cabinetSelectFilter: '',
      allDocumentTypesSelected: false,
      tableFields: [
        {
          key: 'cabinetDescription',
          label: 'Cabinet Description',
          sortable: true,
          tdClass: 'align-middle'
        },
        {
          key: 'documentTypeDescription',
          label: 'Document Type',
          sortable: true,
          tdClass: 'align-middle'
        },
        {
          key: 'versionTypeDescription',
          label: 'Versioning Type',
          sortable: true,
          tdClass: 'align-middle'
        },
        {
          key: 'enabledDescription',
          label: 'Enabled',
          sortable: true,
          tdClass: 'align-middle'
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          tdClass: 'align-middle'
        }
      ],
      detailColumns: ['documentTypeDescription', 'versionTypeDescription', 'enabledDescription', 'actions'],
      groupColumns: ['cabinetDescription', 'actions'],
      tableActions: [
        {
          key: 'edit',
          label: 'Edit',
          variant: 'outline-primary',
          icon: 'edit'
        }
      ],
      exceptionalJobButtons: [
        {
          caption: 'Edit',
          isConfigured: true,
          icon: 'edit',
          variant: 'outline-primary'
        },
        {
          caption: 'Add',
          isConfigured: true,
          icon: 'plus',
          variant: 'outline-success'
        }
      ],
      exceptionalJobDetailButtons: [
        {
          caption: 'Edit',
          isConfigured: true,
          icon: 'edit',
          variant: 'outline-primary'
        }
      ],
      cabinets: [] as any[],
      disableCabinetOptions: false,
      disableDocumentTypeOptions: false,
      filteredCabinets: [] as any[]
    };
  },
  computed: {
    cabinetDropdownText(this: any): string {
      if (!this.cabinets || this.cabinets.length === 0) {
        return 'Select a cabinet';
      }
      const selected = this.cabinets.find((c: any) => c.id === this.cabinetId);
      return selected ? selected.description : 'Select a cabinet';
    },
    filteredCabinetOptions(this: any): Array<{ value: string; text: string }> {
      const filter = this.cabinetSelectFilter ? this.cabinetSelectFilter.toLowerCase() : '';
      return this.filteredCabinets.filter((c: any) => c.description.toLowerCase().includes(filter)).map((c: any) => ({ value: String(c.id), text: c.description }));
    },
    versionTypeOptions(): Array<{ value: ExceptionalJobVersionType; text: string }> {
      return EnumUtils.getEnumValues(ExceptionalJobVersionType).map((item: any) => ({ value: item.key, text: item.description }));
    },
    cabinetOptions(this: any): Array<{ value: string; text: string }> {
      if (!this.cabinets) {
        return [];
      }
      return this.cabinets.map((cabinet: any) => ({
        value: String(cabinet.id),
        text: cabinet.description
      }));
    },
    documentTypeOptions(this: any): Array<{ value: number; text: string }> {
      if (!this.cabinetId) {
        return [];
      }
      const cabinet = this.cabinets.find((c: any) => c.id === this.cabinetId);
      if (!cabinet || !cabinet.documentTypes) {
        return [];
      }
      let options = cabinet.documentTypes.map((dt: any) => ({ value: dt.id, text: dt.description }));

      if (this.modalMode === ExceptionalJobModalState.CreateDocumentType) {
        const group = this.exceptionalJobsGrouped.groupedConfigurations.find((g: any) => g.cabinetId === this.cabinetId);
        if (group && group.configurations) {
          const existingDocTypeIds = group.configurations.map((cfg: any) => cfg.documentTypeId);
          options = options.filter((opt: { value: number; text: string }) => !existingDocTypeIds.includes(opt.value));
        }
      }

      if (this.documentTypeSelectFilter) {
        const filter = this.documentTypeSelectFilter.toLowerCase();
        options = options.filter((opt: { value: number; text: string }) => opt.text.toLowerCase().includes(filter));
      }
      return options;
    },
    documentTypeDropdownText(this: any): string {
      if (!this.documentTypeIds || this.documentTypeIds.length === 0) {
        return 'Select document types';
      }
      const cabinet = this.cabinets.find((c: any) => c.id === this.cabinetId);
      if (!cabinet || !cabinet.documentTypes) {
        return 'Select document types';
      }
      const allOptions = cabinet.documentTypes.map((dt: any) => ({ value: dt.id, text: dt.description }));
      const selected = allOptions.filter((opt: any) => this.documentTypeIds.includes(opt.value));
      return selected.map((opt: any) => opt.text).join(', ');
    },
    documentTypeDropdownToggleClass(this: any) {
      const defaultClasses = ['custom-select', 'text-left', 'text-truncate'];
      const computedClasses = [];
      if (!this.documentTypeIds || this.documentTypeIds.length === 0) {
        computedClasses.push('is-invalid');
      }
      return [...defaultClasses, ...computedClasses];
    },
    exceptionalJobs(this: any): ExceptionalJobConfigurationModel[] {
      if (!this.exceptionalJobsGrouped || !this.exceptionalJobsGrouped.groupedConfigurations) {
        return [];
      }

      const allConfigurations: ExceptionalJobConfigurationModel[] = (this
        .exceptionalJobsGrouped as ExceptionalJobConfigurationCabinetModel).groupedConfigurations!.flatMap(({ configurations, cabinetDescription }) =>
        configurations!.map((x) => ({ ...x, cabinetDescription }))
      );

      return allConfigurations;
    },

    numberOfGroups(this: any): number {
      if (!this.exceptionalJobsGrouped || !this.exceptionalJobsGrouped.groupedConfigurations) {
        return 1;
      }
      return this.exceptionalJobsGrouped.groupedConfigurations.length > 0 ? this.exceptionalJobsGrouped.groupedConfigurations.length : 1;
    },

    showAllDocumentTypesCheckbox(this: any): boolean {
      return this.modalMode !== ExceptionalJobModalState.CreateDocumentType;
    },

    modalTitle(this: any): string {
      switch (this.modalMode) {
        case ExceptionalJobModalState.CreateCabinetConfig:
          return 'Add Cabinet Configuration';
        case ExceptionalJobModalState.CreateDocumentType:
          return 'Add Document Types to Cabinet';
        case ExceptionalJobModalState.EditCabinetConfig:
          return 'Edit Cabinet Configuration';
        case ExceptionalJobModalState.EditDocumentType:
          return 'Edit Document Type Configuration';
        default:
          return 'Configuration';
      }
    }
  },
  methods: {
    async onSaveExceptionalJob(this: any) {
      if (!this.cabinetId || !this.documentTypeIds || !this.versionType) {
        return;
      }
      switch (this.modalMode) {
        case ExceptionalJobModalState.CreateCabinetConfig:
        case ExceptionalJobModalState.CreateDocumentType:
          await this.createExceptionalJobs();
          break;
        case ExceptionalJobModalState.EditCabinetConfig:
          await this.editCabinetExceptionalJobs();
          break;
        case ExceptionalJobModalState.EditDocumentType:
          await this.editDocumentTypeConfiguration();
          break;
      }
    },

    async createExceptionalJobs(this: any) {
      try {
        this.isLoading = true;
        await ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsCreate({
          cabinetId: this.cabinetId,
          documentTypeIds: this.documentTypeIds,
          versionType: this.versionType,
          enabled: this.enabled
        });
        Toast.showSuccess('Exceptional job(s) created successfully.');
        this.showConfigureCabinetModal = false;

        if (!this.exceptionalJobsGrouped.groupedConfigurations) {
          this.exceptionalJobsGrouped.groupedConfigurations = [];
        }
        let group = this.exceptionalJobsGrouped.groupedConfigurations.find((g: any) => g.cabinetId === this.cabinetId);
        if (!group) {
          group = {
            cabinetId: this.cabinetId,
            cabinetDescription: this.cabinetDropdownText,
            configurations: []
          };
          this.exceptionalJobsGrouped.groupedConfigurations.push(group);
        }

        this.documentTypeIds.forEach((dtId: number) => {
          if (!group.configurations.some((cfg: any) => cfg.documentTypeId === dtId)) {
            const docTypeOption = this.documentTypeOptions.find((opt: any) => opt.value === dtId);
            let versionTypeDescription = '';
            const vtOpt = this.versionTypeOptions.find((opt: any) => opt.value === this.versionType);
            if (vtOpt) {
              versionTypeDescription = vtOpt.text;
            } else {
              versionTypeDescription = String(this.versionType);
            }
            group.configurations.push({
              cabinetId: this.cabinetId,
              cabinetDescription: this.cabinetDropdownText,
              documentTypeId: dtId,
              documentTypeDescription: docTypeOption ? docTypeOption.text : `Document Type ${dtId}`,
              versionType: this.versionType,
              versionTypeDescription,
              enabled: this.enabled,
              enabledDescription: this.enabled ? 'Yes' : 'No'
            });
          }
        });
        this.$set(this.exceptionalJobsGrouped, 'groupedConfigurations', [...this.exceptionalJobsGrouped.groupedConfigurations]);
      } catch (error) {
        Toast.showError('Failed to create exceptional job(s).');
      } finally {
        this.isLoading = false;
      }
    },

    async editCabinetExceptionalJobs(this: any) {
      try {
        this.isLoading = true;
        let configToUpdate: any = null;
        const group = this.exceptionalJobsGrouped.groupedConfigurations.find((g: any) => g.cabinetId === this.cabinetId);
        if (group) {
          configToUpdate = group.configurations.find((cfg: any) => this.documentTypeIds.includes(cfg.documentTypeId));
        }
        if (!configToUpdate) {
          Toast.showError('No configuration found to update.');
          return;
        }

        await ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsUpdate({
          id: configToUpdate.id,
          cabinetId: this.cabinetId,
          documentTypeIds: this.documentTypeIds,
          versionType: this.versionType,
          enabled: this.enabled
        });
        Toast.showSuccess('Exceptional job(s) updated successfully.');
        this.showConfigureCabinetModal = false;

        if (group) {
          group.configurations = group.configurations.filter((cfg: any) => this.documentTypeIds.includes(cfg.documentTypeId));
          group.configurations.forEach((cfg: any) => {
            cfg.versionType = this.versionType;
            const vtOpt = this.versionTypeOptions.find((opt: any) => opt.value === this.versionType);
            cfg.versionTypeDescription = vtOpt ? vtOpt.text : String(this.versionType);
            cfg.enabled = this.enabled;
            cfg.enabledDescription = this.enabled ? 'Yes' : 'No';
          });

          this.documentTypeIds.forEach((dtId: number) => {
            if (!group.configurations.some((cfg: any) => cfg.documentTypeId === dtId)) {
              const docTypeOption = this.documentTypeOptions.find((opt: any) => opt.value === dtId);
              const vtOpt = this.versionTypeOptions.find((opt: any) => opt.value === this.versionType);
              group.configurations.push({
                cabinetId: this.cabinetId,
                cabinetDescription: group.cabinetDescription,
                documentTypeId: dtId,
                documentTypeDescription: docTypeOption ? docTypeOption.text : `Document Type ${dtId}`,
                versionType: this.versionType,
                versionTypeDescription: vtOpt ? vtOpt.text : String(this.versionType),
                enabled: this.enabled,
                enabledDescription: this.enabled ? 'Yes' : 'No'
              });
            }
          });

          if (group.configurations.length === 0) {
            this.exceptionalJobsGrouped.groupedConfigurations = this.exceptionalJobsGrouped.groupedConfigurations.filter((g: any) => g.cabinetId !== this.cabinetId);
          } else {
            this.$set(this.exceptionalJobsGrouped, 'groupedConfigurations', [...this.exceptionalJobsGrouped.groupedConfigurations]);
          }
        }
      } catch (error) {
        Toast.showError('Failed to update exceptional job(s).');
      } finally {
        this.isLoading = false;
      }
    },

    async editDocumentTypeConfiguration(this: any) {
      try {
        this.isLoading = true;
        const group = this.exceptionalJobsGrouped.groupedConfigurations.find((g: any) => g.cabinetId === this.cabinetId);
        if (!group) {
          Toast.showError('Group could not be found for cabinet.');
          return;
        }
        if (this.documentTypeIds.length !== 1) {
          Toast.showError('Please select exactly one document type to update.');
          return;
        }
        const documentTypeId = this.documentTypeIds[0];
        const configToUpdate = group.configurations.find((cfg: any) => cfg.documentTypeId === documentTypeId);
        if (!configToUpdate) {
          Toast.showError('No configuration found to update.');
          return;
        }

        await ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsJobUpdate({
          id: configToUpdate.id,
          cabinetId: this.cabinetId,
          documentTypeId,
          versionType: this.versionType,
          enabled: this.enabled
        });

        configToUpdate.versionType = this.versionType;
        const vtOpt = this.versionTypeOptions.find((opt: any) => opt.value === this.versionType);
        configToUpdate.versionTypeDescription = vtOpt ? vtOpt.text : String(this.versionType);
        configToUpdate.enabled = this.enabled;
        configToUpdate.enabledDescription = this.enabled ? 'Yes' : 'No';
        Toast.showSuccess('Exceptional job configuration updated successfully.');
        this.showConfigureCabinetModal = false;
        this.$set(this.exceptionalJobsGrouped, 'groupedConfigurations', [...this.exceptionalJobsGrouped.groupedConfigurations]);
      } catch (error) {
        Toast.showError('Failed to update exceptional job configuration.');
      } finally {
        this.isLoading = false;
      }
    },

    selectCabinet(this: any, cabinetId: string) {
      this.cabinetId = cabinetId;
      this.documentTypeIds = [];
      this.allDocumentTypesSelected = false;
      this.documentTypeSelectFilter = '';
    },

    onSearchQueryUpdate(this: any, key: string, value: string) {
      this.searchQuery = value;
    },

    async refreshData(this: any) {
      this.isLoading = true;
      try {
        this.exceptionalJobsGrouped = await ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsList();
        this.cabinets = this.exceptionalJobsGrouped.cabinets;
        const configuredCabinetIds = this.exceptionalJobsGrouped.groupedConfigurations.map((g: any) => String(g.cabinetId));
        this.filteredCabinets = this.cabinets.filter((c: any) => !configuredCabinetIds.includes(String(c.id)));
      } catch (error) {
        Toast.showError('Failed to load exceptional jobs configuration');
      } finally {
        this.isLoading = false;
      }
    },

    onConfigureAdditionalCabinet(this: any) {
      this.modalMode = ExceptionalJobModalState.CreateCabinetConfig;
      this.cabinetId = '';
      this.documentTypeIds = [];
      this.versionType = ExceptionalJobVersionType.CreateNewVersion;
      this.enabled = false;
      this.disableCabinetOptions = false;
      this.disableDocumentTypeOptions = false;
      this.documentTypeSelectFilter = '';
      const configuredCabinetIds = (this.exceptionalJobsGrouped.groupedConfigurations || []).map((g: any) => String(g.cabinetId));
      this.filteredCabinets = this.cabinets.filter((c: any) => !configuredCabinetIds.includes(String(c.id)));
      this.showConfigureCabinetModal = true;
    },

    handleGroupAction(this: any, action: any, selectedItems: ExceptionalJobConfigurationModel[]) {
      if (action.caption === 'Edit') {
        const cabinetId = selectedItems[0].cabinetId;
        this.modalMode = ExceptionalJobModalState.EditCabinetConfig;
        this.cabinetId = cabinetId;
        this.documentTypeSelectFilter = '';
        const group = this.exceptionalJobsGrouped.groupedConfigurations.find((g: any) => g.cabinetId === cabinetId);
        if (group) {
          this.documentTypeIds = group.configurations.map((cfg: any) => cfg.documentTypeId);
          this.versionType = ExceptionalJobVersionType.CreateNewVersion;
          this.enabled = group.configurations.every((x: any) => x.enabled === true);
          this.disableCabinetOptions = true;
          this.disableDocumentTypeOptions = false;
        }
        this.showConfigureCabinetModal = true;
      } else if (action.caption === 'Add') {
        const cabinetId = selectedItems[0].cabinetId;
        this.modalMode = ExceptionalJobModalState.CreateDocumentType;
        this.cabinetId = cabinetId;
        this.documentTypeIds = [];
        this.versionType = ExceptionalJobVersionType.CreateNewVersion;
        this.enabled = false;
        this.disableCabinetOptions = true;
        this.disableDocumentTypeOptions = false;
        this.documentTypeSelectFilter = '';
        this.showConfigureCabinetModal = true;
      }
    },

    handleDetailRowAction(this: any, event: any, item: ExceptionalJobConfigurationModel) {
      if (event.caption === 'Edit') {
        this.modalMode = ExceptionalJobModalState.EditDocumentType;
        this.disableCabinetOptions = true;
        this.disableDocumentTypeOptions = true;
        this.cabinetId = item.cabinetId;
        this.documentTypeIds = [item.documentTypeId];
        this.enabled = item.enabled;
        this.versionType = item.versionType;
        this.documentTypeSelectFilter = '';
        this.showConfigureCabinetModal = true;
      }
    },

    async showEditCabinetExceptionalJobsModal(this: any, cabinetId: string) {
      await ModalHelpers.methods.showInformationModalAsync.call(this, this.$bvModal, `Edit Cabinet Exceptional Jobs modal for cabinet ${cabinetId} not yet implemented`);
    },

    async editExceptionalJobModal(this: any, item: ExceptionalJobConfigurationModel) {
      const documentTypeName = item.documentTypeDescription;
      await ModalHelpers.methods.showInformationModalAsync.call(this, this.$bvModal, `Edit Exceptional Job modal for ${documentTypeName} not yet implemented`);
    },

    async showDisableCabinetConfigurationsModal(this: any, configurations: ExceptionalJobConfigurationModel[], cabinetDescription: string) {
      const confirmed = await ModalHelpers.methods.showConfirmationModalAsync.call(
        this,
        this.$bvModal,
        `Are you sure you want to disable all exceptional job configurations for cabinet "${cabinetDescription}"?`,
        {
          title: 'Disable Cabinet Configurations',
          okTitle: 'Disable',
          okVariant: ColourVariantType.Danger
        }
      );

      if (!confirmed) {
        return;
      }

      try {
        this.isLoading = true;

        const updatePromises = configurations.map((config) =>
          ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsUpdate({
            id: config.id,
            cabinetId: config.cabinetId,
            documentTypeId: config.documentTypeId,
            versionType: config.versionType,
            enabled: false
          } as UpdateExceptionalJobConfigurationModel)
        );

        await Promise.all(updatePromises);

        Toast.showSuccess(`Successfully disabled all exceptional job configurations for cabinet "${cabinetDescription}"`);
        await this.refreshData();
      } catch (error) {
        console.error('Failed to disable cabinet configurations:', error);
        Toast.showError('Failed to disable cabinet configurations');
      } finally {
        this.isLoading = false;
      }
    },

    async disableExceptionalJob(this: any, item: ExceptionalJobConfigurationModel) {
      const documentTypeName = item.documentTypeDescription || 'Unknown Document Type';
      const confirmed = await ModalHelpers.methods.showConfirmationModalAsync.call(
        this,
        this.$bvModal,
        `Are you sure you want to disable the exceptional job configuration for "${documentTypeName}"?`,
        {
          title: 'Disable Configuration',
          okTitle: 'Disable',
          okVariant: ColourVariantType.Danger
        }
      );

      if (!confirmed) {
        return;
      }

      try {
        this.isLoading = true;

        await ApiConfiguration.apiWithErrorPage().exceptionalJobs.exceptionalJobsUpdate({
          id: item.id,
          cabinetId: item.cabinetId,
          documentTypeId: item.documentTypeId,
          versionType: item.versionType,
          enabled: false
        } as UpdateExceptionalJobConfigurationModel);

        Toast.showSuccess(`Successfully disabled exceptional job configuration for "${documentTypeName}"`);
        await this.refreshData();
      } catch (error) {
        console.error('Failed to disable configuration:', error);
        Toast.showError('Failed to disable configuration');
      } finally {
        this.isLoading = false;
      }
    },
    toggleAllDocumentTypes(this: any, checked: boolean) {
      const allIds = this.documentTypeOptions.map((opt: { value: number; text: string }) => opt.value);
      if (checked) {
        this.documentTypeIds = [...allIds];
      } else {
        this.documentTypeIds = [];
      }
    },
    onDocumentTypeCheckboxChange(this: any) {
      const cabinet = this.cabinets.find((c: any) => c.id === this.cabinetId);
      const allIds = cabinet?.documentTypes ? cabinet.documentTypes.map((dt: any) => dt.id) : [];
      const selectedIds = this.documentTypeIds;
      this.allDocumentTypesSelected = allIds.length > 0 && selectedIds.length === allIds.length && allIds.every((id: number) => selectedIds.includes(id));
    }
  },
  async mounted(this: any) {
    await this.refreshData();
  }
});
